/**
 * usePushSoftAsk — the single client seam every push soft-ask surface calls
 * before showing its UI (R3 Phase 4). Wires the pure orchestrator
 * (services/pushPermissionOrchestrator.ts) to live app state:
 *
 * - Remote Config: `push_soft_ask_enabled` (master) + `push_soft_ask_config`
 *   (per-surface JSON, defaults applied client-side)
 * - NotificationContext: current OS permission state
 * - Firestore: unified soft-ask history on userContent/{uid} (legacy
 *   per-surface fields seed it — see historyFromUserDoc)
 * - Session latch: at most one proactive soft-ask per app session
 *
 * Surfaces keep their own legacy "already answered" reads/writes (dual-write);
 * this hook adds the cross-surface rules and the unified record.
 */

import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Capacitor } from '@capacitor/core';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@shared/services/firebase';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { useRemoteConfig } from '../context/RemoteConfigContext';
import {
  claimSessionLatch,
  decideSoftAsk,
  getSessionLatch,
  historyFromUserDoc,
  parsePushSoftAskConfig,
  recordUnifiedSoftAskResponse,
  subscribeSessionLatch,
  type OsPromptResult,
  type SoftAskDecisionReason,
  type SoftAskRecord,
  type SoftAskResponse,
  type SoftAskSurface,
} from '../services/pushPermissionOrchestrator';

export interface UsePushSoftAskResult {
  /** May this surface show its soft-ask UI right now? */
  mayShow: boolean;
  /** The rule that produced the decision (for logging/analytics). */
  reason: SoftAskDecisionReason;
  /** True while the unified history read is still in flight. */
  isLoading: boolean;
  /**
   * Claim the one-per-session slot. Call once when the surface actually
   * renders its ask (not merely when it becomes eligible). No-op for
   * user-initiated surfaces.
   */
  markShown: () => void;
  /**
   * Append the unified response record (additive — the caller still writes
   * its legacy per-surface fields). Fire-and-forget safe; never throws.
   */
  recordResponse: (response: SoftAskResponse, osPromptResult?: OsPromptResult) => Promise<void>;
}

export function usePushSoftAsk(surface: SoftAskSurface): UsePushSoftAskResult {
  const { currentUser } = useAuth();
  const { pushSoftAskEnabled, pushSoftAskConfig } = useRemoteConfig();
  const { permissionStatus } = useNotifications();

  const uid = currentUser?.uid ?? null;

  // Unified history — one read per uid. `undefined` = in flight. A failed
  // read degrades to empty history (fail-open): the unified rules are
  // additive throttles, and each surface's legacy "already answered" guard
  // still fails closed on its own read.
  const [history, setHistory] = useState<SoftAskRecord[] | undefined>(undefined);
  useEffect(() => {
    if (!uid) {
      setHistory([]);
      return;
    }
    let cancelled = false;
    setHistory(undefined);
    getDoc(doc(db, 'userContent', uid))
      .then((snap) => {
        if (!cancelled) setHistory(historyFromUserDoc(snap.exists() ? snap.data() : undefined));
      })
      .catch(() => {
        if (!cancelled) setHistory([]);
      });
    return () => {
      cancelled = true;
    };
  }, [uid]);

  // Re-render every consulted surface when any surface claims the latch.
  const latchedSurface = useSyncExternalStore(subscribeSessionLatch, getSessionLatch);

  const config = useMemo(() => parsePushSoftAskConfig(pushSoftAskConfig), [pushSoftAskConfig]);

  const decision = useMemo(
    () =>
      decideSoftAsk({
        surface,
        isNative: Capacitor.isNativePlatform(),
        permissionStatus,
        masterEnabled: pushSoftAskEnabled,
        config,
        history: history ?? [],
        sessionLatchedSurface: latchedSurface,
      }),
    [surface, permissionStatus, pushSoftAskEnabled, config, history, latchedSurface],
  );

  const isLoading = history === undefined;
  // While the history read is in flight, proactive surfaces hold off (their
  // legacy reads make them render nothing during this window anyway);
  // user-initiated surfaces are unaffected by history so they never wait.
  const mayShow = decision.mayShow && (!isLoading || decision.reason !== 'eligible');

  const markShown = useCallback(() => {
    const cfg = config.surfaces[surface];
    if (cfg?.userInitiated) return;
    claimSessionLatch(surface);
  }, [config, surface]);

  const recordResponse = useCallback(
    async (response: SoftAskResponse, osPromptResult?: OsPromptResult) => {
      const record: SoftAskRecord = {
        surface,
        response,
        ...(osPromptResult ? { osPromptResult } : {}),
        at: new Date().toISOString(),
      };
      // Keep in-session decisions honest (cooldowns apply immediately).
      setHistory((prev) => [...(prev ?? []), record]);
      if (!uid) return;
      await recordUnifiedSoftAskResponse(uid, record);
    },
    [surface, uid],
  );

  return { mayShow, reason: decision.reason, isLoading, markShown, recordResponse };
}
